/**
 * SportsClubsList — /sports/clubs
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Users, MapPin, PlusCircle, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import SportsCategoryNav from "@/components/SportsCategoryNav";

export default function SportsClubsList() {
  const [params, setParams] = useSearchParams();
  const sport = params.get("sport") || "";
  const country = params.get("country") || "";
  const city = params.get("city") || "";
  const q = params.get("q") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { http.get("/sports/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (sport) p.sport = sport;
    if (country) p.country = country;
    if (city) p.city = city;
    if (q) p.q = q;
    http.get("/sports/clubs", { params: p })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [sport, country, city, q]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <>
      <SportsCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="clubs-list-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="overline mb-1">TEAMS &amp; CLUBS</div>
            <h1 className="font-display text-4xl mb-1">Sports clubs</h1>
            <p className="opacity-70">{total} listed</p>
          </div>
          <Link to="/sports/clubs/new" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="cta-new-club">
            <PlusCircle className="w-4 h-4" /> Register your club
          </Link>
        </div>

        <div className="wofy-card !p-3 mb-6 flex flex-wrap gap-2">
          <select value={sport} onChange={e => setFilter("sport", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-sport">
            <option value="">All sports</option>
            {meta?.categories?.map(cat => (
              <optgroup key={cat.key} label={cat.label}>
                {cat.sports.map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
              </optgroup>
            ))}
          </select>
          <input value={country} onChange={e => setFilter("country", e.target.value)}
            placeholder="Country" className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <input value={city} onChange={e => setFilter("city", e.target.value)}
            placeholder="City" className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <input value={q} onChange={e => setFilter("q", e.target.value)}
            placeholder="Search…" className="text-sm px-2 py-1 rounded border flex-1 min-w-[8rem]"
            style={{ borderColor: "var(--wofy-border)" }} />
        </div>

        {loading ? (
          <div className="opacity-60 text-sm inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center">
            <p className="opacity-70 mb-3">No clubs match. Be the first to register.</p>
            <Link to="/sports/clubs/new" className="wofy-btn-primary inline-flex items-center gap-1.5">
              <PlusCircle className="w-4 h-4" /> Register club
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(c => (
              <Link key={c.id} to={`/sports/clubs/${c.slug}`}
                className="wofy-card !p-4 hover:shadow-md transition block"
                data-testid={`club-card-${c.slug}`}>
                <div className="flex items-center gap-3 mb-2">
                  {c.logo_url ? (
                    <img src={c.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-black/5 grid place-items-center text-xs font-mono">
                      {(c.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
                      <Users className="w-3 h-3 inline mr-1" />{c.sport}
                    </div>
                  </div>
                </div>
                {c.description && <p className="text-sm opacity-80 line-clamp-2">{c.description}</p>}
                {(c.city || c.country) && (
                  <div className="text-xs opacity-70 flex items-center gap-1 mt-2">
                    <MapPin className="w-3 h-3" />{[c.city, c.state, c.country].filter(Boolean).join(", ")}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
